import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import styles from "./Flashcards.module.css";
import LoadingSpinner from "../components/LoadingSpinner";
import FeedbackModal from "../components/FeedbackModal";
import useGameLogic from "../hooks/useGameLogic";
import GamePopups from "../components/GamePopups";
import commonGameStyles from "./GameCommon.module.css";

// --- Feedback Modal Constants (UNIQUE FOR FLASHCARDS) ---
const FEEDBACK_FORM_URL =
    "https://docs.google.com/forms/d/e/1FAIpQLSfCYQQ3NJS3nRLoVB9Q1LtELQxQizAD37t1LMwJ_5-ei2dryA/viewform?usp=dialog";
const FEEDBACK_PROMPT_INTERVAL = 3;
const FEEDBACK_PROMPT_DISMISS_DAYS = 7;

// Local Storage Keys (unique for Flashcards)
const LS_KEY_GAMES_PLAYED = "flashcards_games_played_count";
const LS_KEY_LAST_PROMPT_TIME = "flashcards_last_prompt_time";
const LS_KEY_DISMISSED_PERMANENTLY = "flashcards_dismissed_permanently";
const LS_KEY_FLASHCARDS_FIRST_COMPLETED = "flashcards_first_completed";

// Define available surahs for selection
const AVAILABLE_SURAHS = [
    { id: 1, name: "Surah Al-Fatiha (The Opening)" },
    { id: 108, name: "Surah Al-Kawthar (The Abundance)" },
    { id: 110, name: "Surah An-Nasr (The Help)" },
    { id: 112, name: "Surah Al-Ikhlas (The Sincerity)" },
    { id: 114, name: "Surah An-Nas (Mankind)" },
];

const CARD_SUIT_ICONS = [
    { symbol: "♠", color: "#333333" },
    { symbol: "♥", color: "#E74C3C" },
    { symbol: "♦", color: "#2ECC71" },
    { symbol: "♣", color: "#3498DB" },
];

const QUESTION_TIMER_DURATION = 15;
const REVIEW_PERIOD_DURATION = 3000;

function Flashcards({ inJourney = false, onComplete }) {
    const { user } = useAuth();
    const navigate = useNavigate();
    const backendApiUrl = process.env.REACT_APP_BACKEND_API_URL;
    const ACTIVITY_TYPE = "flashcards";

    // Core game state
    const [score, setScore] = useState(0);
    const [done, setDone] = useState(false);

    // useGameLogic hook (always called at top-level)
    const {
        lives,
        showGameOverPopup,
        showStreakPopup,
        streakMessage,
        showEncouragementPopup,
        encouragementMessage,
        showFeedbackModal,
        handleCloseFeedbackModal,
        handleDismissFeedbackPermanently,
        handleCorrectAttempt,
        handleIncorrectAttempt,
        resetGameLogicStates,
        redirectMessage,
        questions,
        current,
        setCurrent,
        loadingQuestions,
        error,
        loadQuizQuestions,
    } = useGameLogic({
        activityType: ACTIVITY_TYPE,
        initialLives: 5,
        gameSpecificEndCondition: done,
        currentScore: score,
        feedbackConfig: {
            FEEDBACK_FORM_URL,
            FEEDBACK_PROMPT_INTERVAL,
            FEEDBACK_PROMPT_DISMISS_DAYS,
            LS_KEY_GAMES_PLAYED,
            LS_KEY_LAST_PROMPT_TIME,
            LS_KEY_DISMISSED_PERMANENTLY,
            LS_KEY_FLASHCARDS_FIRST_COMPLETED,
        },
        navigate,
    });

    // Local UI state
    const [selectedOption, setSelectedOption] = useState(null);
    const [showFeedback, setShowFeedback] = useState(false);
    const [scoreSubmitted, setScoreSubmitted] = useState(false);
    const [showSurahDeckCompleteAnimation, setShowSurahDeckCompleteAnimation] = useState(false);
    const [cardAnimationPhase, setCardAnimationPhase] = useState("idle");
    const [showInstructions, setShowInstructions] = useState(true);
    const [showSurahSelection, setShowSurahSelection] = useState(false);
    const [selectedSurahId, setSelectedSurahId] = useState(null);
    const [showFullSurahReveal, setShowFullSurahReveal] = useState(false);
    const [fullSurahWordsData, setFullSurahWordsData] = useState(null);
    const [timeLeft, setTimeLeft] = useState(QUESTION_TIMER_DURATION);

    // Refs for timers/animations
    const completionAnimationTimeoutRef = useRef(null);
    const animationTimeoutRef = useRef(null);
    const timerRef = useRef(null);
    const feedbackAnimationTimeoutRef = useRef(null);

    // -------------------------------
    // CALLABLE LOGIC (unconditional)
    // -------------------------------

    // Submit scoreboard — only actually runs when NOT inJourney (guard inside)
    const submitScoreToLeaderboard = useCallback(
        async (finalScore) => {
            if (!user || scoreSubmitted || inJourney) return;
            if (!user.uid || !user.username) {
                console.error("User not logged in or missing user details. Cannot submit score.");
                return;
            }
            try {
                await axios.post(`${backendApiUrl}/scores`, {
                    userId: user.uid,
                    username: user.username,
                    score: finalScore,
                    activityType: ACTIVITY_TYPE,
                    avatar: user.avatar || user.photoURL || "/default-avatar.png",
                });
                setScoreSubmitted(true);
                // console.log('Flashcards score submitted successfully!');
            } catch (err) {
                console.error("Failed to submit Flashcards score:", err.response ? err.response.data : err.message);
            }
        },
        [user, scoreSubmitted, ACTIVITY_TYPE, backendApiUrl, inJourney]
    );

    // Move to next flashcard
    const handleNextQuestion = useCallback(() => {
        if (feedbackAnimationTimeoutRef.current) {
            clearTimeout(feedbackAnimationTimeoutRef.current);
            feedbackAnimationTimeoutRef.current = null;
        }
        if (showGameOverPopup) return;

        setSelectedOption(null);
        setShowFeedback(false);
        setTimeLeft(QUESTION_TIMER_DURATION);

        if (current + 1 >= questions.length) {
            // completed current deck
            setShowSurahDeckCompleteAnimation(true);
            completionAnimationTimeoutRef.current = setTimeout(() => {
                setShowSurahDeckCompleteAnimation(false);
                setDone(true);
                setShowFullSurahReveal(true);
            }, 3000);
        } else {
            setCardAnimationPhase("slide-out");
            animationTimeoutRef.current = setTimeout(() => {
                setCurrent((prev) => prev + 1);
                setCardAnimationPhase("idle");
            }, 400);
        }
    }, [current, questions.length, showGameOverPopup, setCurrent]);

    // Handle selecting an option
    const handleOptionClick = useCallback(
        async (option) => {
            if (selectedOption !== null || showGameOverPopup) return;

            if (timerRef.current) {
                clearInterval(timerRef.current);
            }

            const currentQ = questions[current];
            const isCorrect = option !== null && currentQ && option === currentQ.correctAnswerText;

            setSelectedOption(option);
            setShowFeedback(true);

            if (isCorrect) {
                setScore((prev) => prev + 1);
                handleCorrectAttempt(currentQ._id);
            } else {
                handleIncorrectAttempt(currentQ._id);
            }
        },
        [selectedOption, questions, current, showGameOverPopup, handleCorrectAttempt, handleIncorrectAttempt]
    );

    // Fetch full surah data for review screen
    const fetchFullSurahWords = useCallback(
        async (surahId) => {
            try {
                const response = await axios.get(`${backendApiUrl}/flashcards/full-surah/${surahId}`);
                setFullSurahWordsData(response.data);
            } catch (err) {
                console.error("Failed to fetch full surah words:", err);
                setFullSurahWordsData([]);
            }
        },
        [backendApiUrl]
    );

    // Prepare/seed questions for a surah
    const prepareQuiz = useCallback(
        async (surahId) => {
            resetGameLogicStates();
            setScore(0);
            setDone(false);
            setScoreSubmitted(false);
            setFullSurahWordsData(null);
            setShowFullSurahReveal(false);
            setCardAnimationPhase("idle");
            setTimeLeft(QUESTION_TIMER_DURATION);
            setSelectedOption(null);
            setShowFeedback(false);

            try {
                // loadQuizQuestions(surahId, false) expected by hook to fetch questions for a surah
                await loadQuizQuestions(surahId, false);
                setShowInstructions(false);
                setShowSurahSelection(false);
            } catch (err) {
                console.error("Flashcards: Error preparing questions:", err);
            }
        },
        [resetGameLogicStates, loadQuizQuestions]
    );

    // -------------------------------
    // EFFECTS (unconditional declarations)
    // -------------------------------

    // Show instructions when user becomes available
    useEffect(() => {
        if (user) {
            setShowInstructions(true);
        }
    }, [user]);

    // Timer effect — runs while showing questions
    useEffect(() => {
        const shouldRunTimer =
            !showInstructions &&
            !showSurahSelection &&
            questions.length > 0 &&
            !done &&
            !showFullSurahReveal &&
            !showFeedback &&
            selectedOption === null &&
            !showGameOverPopup;

        if (shouldRunTimer) {
            setTimeLeft(QUESTION_TIMER_DURATION);
            if (timerRef.current) clearInterval(timerRef.current);

            timerRef.current = setInterval(() => {
                setTimeLeft((prevTime) => {
                    if (prevTime <= 1) {
                        clearInterval(timerRef.current);
                        // mark incorrect and proceed
                        if (questions[current] && questions[current]._id) {
                            handleIncorrectAttempt(questions[current]._id);
                        }
                        // emulate selecting null option (timeout)
                        handleOptionClick(null);
                        return 0;
                    }
                    return prevTime - 1;
                });
            }, 1000);
        } else {
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
        }

        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
        };
    }, [
        current,
        showInstructions,
        showSurahSelection,
        questions.length,
        done,
        showFullSurahReveal,
        showFeedback,
        selectedOption,
        handleOptionClick,
        showGameOverPopup,
        handleIncorrectAttempt,
        questions,
    ]);

    // Auto-advance after feedback popup
    useEffect(() => {
        if (showFeedback && !showGameOverPopup) {
            if (feedbackAnimationTimeoutRef.current) {
                clearTimeout(feedbackAnimationTimeoutRef.current);
            }
            feedbackAnimationTimeoutRef.current = setTimeout(() => {
                handleNextQuestion();
            }, REVIEW_PERIOD_DURATION);
        }
        return () => {
            if (feedbackAnimationTimeoutRef.current) {
                clearTimeout(feedbackAnimationTimeoutRef.current);
                feedbackAnimationTimeoutRef.current = null;
            }
        };
    }, [showFeedback, handleNextQuestion, showGameOverPopup]);

    // When done: if inJourney -> trigger onComplete; else submit score
    useEffect(() => {
        if (!done) return;

        if (inJourney) {
            // In journey: call onComplete so JourneyPage can advance/unmount this component.
            if (typeof onComplete === "function") {
                onComplete();
            }
        } else {
            // Standalone: submit score once (if user present)
            if (user && !scoreSubmitted) {
                submitScoreToLeaderboard(score);
            }
        }
    }, [done, inJourney, onComplete, user, score, scoreSubmitted, submitScoreToLeaderboard]);

    // Fetch full surah data when showing the reveal
    useEffect(() => {
        if (showFullSurahReveal && !fullSurahWordsData) {
            fetchFullSurahWords(selectedSurahId);
        }
    }, [showFullSurahReveal, fullSurahWordsData, selectedSurahId, fetchFullSurahWords]);

    // Clean up timers on unmount
    useEffect(() => {
        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
            if (feedbackAnimationTimeoutRef.current) clearTimeout(feedbackAnimationTimeoutRef.current);
            if (completionAnimationTimeoutRef.current) clearTimeout(completionAnimationTimeoutRef.current);
            if (animationTimeoutRef.current) clearTimeout(animationTimeoutRef.current);
        };
    }, []);

    // -------------------------------
    // UI helpers & callbacks
    // -------------------------------

    const backgroundStyle = { backgroundImage: `url("/images/flashcards.jpg")` };

    const proceedToSurahSelection = useCallback(() => {
        setShowInstructions(false);
        setShowSurahSelection(true);
    }, []);

    const handleSurahSelect = useCallback(
        (surahId) => {
            setSelectedSurahId(surahId);
            prepareQuiz(surahId);
        },
        [prepareQuiz]
    );

    // -------------------------------
    // RENDER
    // -------------------------------

    // Early return for not logged-in users (hooks already called)
    if (!user) {
        return <p className={styles.message}>Please log in to use this feature.</p>;
    }

    // Loading / error UI
    if (loadingQuestions) {
        return (
            <div className={styles.background} style={backgroundStyle}>
                <div className={styles.overlay}></div>
                <div className={styles.flashcardDeckWrapper}>
                    <div className={styles.container}>
                        <LoadingSpinner />
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles.background} style={backgroundStyle}>
                <div className={styles.overlay}></div>
                <div className={styles.flashcardDeckWrapper}>
                    <div className={styles.container}>
                        <p className={styles.errorMessage}>Error: {error}</p>
                        <button
                            onClick={() => {
                                if (selectedSurahId) {
                                    prepareQuiz(selectedSurahId);
                                } else {
                                    setShowSurahSelection(true);
                                }
                            }}
                            className={styles.proceedButton}
                        >
                            Retry Loading or Choose Surah
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const currentQ = questions.length > 0 ? questions[current] : null;
    const isCorrect = currentQ && selectedOption !== null ? selectedOption === currentQ.correctAnswerText : false;
    const currentCardIcon = CARD_SUIT_ICONS[current % CARD_SUIT_ICONS.length];

    return (
        <div className={styles.background} style={backgroundStyle}>
            <div className={styles.overlay}></div>
            <div className={styles.flashcardDeckWrapper}>
                {/* decorative stacked cards (kept from original) */}
                <div className={`${styles.behindCard} ${styles["card-6"]}`} />
                <div className={`${styles.behindCard} ${styles["card-5"]}`} />
                <div className={`${styles.behindCard} ${styles["card-4"]}`} />
                <div className={`${styles.behindCard} ${styles["card-3"]}`} />
                <div className={`${styles.behindCard} ${styles["card-2"]}`} />
                <div className={`${styles.behindCard} ${styles["card-1"]}`} />

                <div className={styles.container}>
                    <h2 className={styles.title}>⚡️Surah Flashcards</h2>

                    {/* Instructions */}
                    {showInstructions ? (
                        <div className={styles.instructionsPopup}>
                            <h3>How to Play Flashcards:</h3>
                            <ul>
                                <li>Arabic words making a surah will be displayed.</li>
                                <li>Choose the correct English meaning from the options.</li>
                                <li>You have 15 seconds to answer each question.</li>
                                <li>At the end, review the full Surah.</li>
                            </ul>
                            <button className={styles.proceedButton} onClick={proceedToSurahSelection}>
                                Proceed
                            </button>
                        </div>
                    ) : showSurahSelection ? (
                        <div className={styles.surahSelection}>
                            <h3>Choose a Surah:</h3>
                            <div className={styles.surahOptions}>
                                {AVAILABLE_SURAHS.map((surah) => (
                                    <button
                                        key={surah.id}
                                        className={styles.surahButton}
                                        onClick={() => handleSurahSelect(surah.id)}
                                        disabled={showGameOverPopup}
                                    >
                                        {surah.name}
                                    </button>
                                ))}
                            </div>
                        </div>
                    ) : showSurahDeckCompleteAnimation ? (
                        <div className={styles.surahDeckCompleteAnimation}>
                            <h3>Surah Deck Complete!</h3>
                            <p>MashaAllah! You've mastered this Surah's meanings. ✨</p>
                        </div>
                    ) : (done || showGameOverPopup) ? (
                        // RESULT SCREEN
                        !inJourney ? (
                            <div className={styles.result}>
                                {showGameOverPopup ? (
                                    <>
                                        <h3 className={styles.gameOverTitle}>Game Over! 💔</h3>
                                        <p className={styles.gameOverMessage}>Aim for better next time Insha'Allah.</p>
                                        <p className={styles.finalScore}>Your score: {score} / {questions.length}</p>
                                    </>
                                ) : (
                                    <>
                                        <h3>Your Score: {score} / {questions.length}</h3>
                                        <p>Great effort! 🌟</p>
                                    </>
                                )}

                                {scoreSubmitted ? (
                                    <p className={styles.successMessage}>Score submitted to leaderboard!</p>
                                ) : (
                                    <p className={styles.submittingMessage}>Submitting score...</p>
                                )}

                                {redirectMessage && (
                                    <p className={redirectMessage.includes("Redirecting") ? styles.successMessage : styles.errorMessage}>
                                        {redirectMessage}
                                    </p>
                                )}

                                <div style={{ marginTop: 15 }}>
                                    <button onClick={() => navigate("/home")} className={styles.proceedButton}>
                                        Back to Home
                                    </button>
                                    {showGameOverPopup && (
                                        <button
                                            onClick={() => {
                                                resetGameLogicStates();
                                                prepareQuiz(selectedSurahId);
                                            }}
                                            className={styles.proceedButton}
                                            style={{ marginLeft: 10 }}
                                        >
                                            Play Again
                                        </button>
                                    )}
                                </div>
                            </div>
                        ) : null // inJourney: render nothing — journey will unmount this component via onComplete
                    ) : showFullSurahReveal ? (
                        // FULL SURAH REVIEW (only in standalone)
                        !inJourney ? (
                            <div className={styles.fullSurahReveal}>
                                <h3>{AVAILABLE_SURAHS.find((s) => s.id === selectedSurahId)?.name || "Full Surah Review"}</h3>
                                {fullSurahWordsData ? (
                                    <div className={styles.surahContent}>
                                        {fullSurahWordsData.map((wordData, index) => (
                                            <span key={wordData._id || `${index}`} className={wordData.arabicWord ? styles.surahArabic : ""}>
                        {wordData.arabicWord}
                                                <span className={styles.surahTransliteration}>({wordData.transliteration})</span>
                        <span className={styles.surahMeaning}>{wordData.meaning}</span>
                                                {index + 1 < fullSurahWordsData.length && wordData.ayah !== fullSurahWordsData[index + 1].ayah && <br className={styles.ayahBreak} />}
                                                {index + 1 < fullSurahWordsData.length && wordData.ayah === fullSurahWordsData[index + 1].ayah && " "}
                      </span>
                                        ))}
                                    </div>
                                ) : (
                                    <LoadingSpinner message="Loading Surah for review..." />
                                )}
                                <p className={styles.redirectMessage}>{redirectMessage || "Reviewing the Surah..."}</p>
                                <button onClick={() => navigate("/")} className={styles.proceedButton} style={{ marginTop: 15 }}>
                                    Back to Home
                                </button>
                            </div>
                        ) : null
                    ) : (
                        // MAIN FLASHCARD QUESTION UI
                        questions.length > 0 ? (
                            <div key={current} className={`${styles.flashcardContent} ${styles[cardAnimationPhase]}`}>
                                {!showInstructions && !showGameOverPopup && (
                                    <>
                                        <p className={styles.timerText}>Time Left: {timeLeft}s</p>
                                        <div className={commonGameStyles.livesContainer}>
                                            {[...Array(5)].map((_, i) => (
                                                <span
                                                    key={i}
                                                    className={`${commonGameStyles.heartIcon} ${i < lives ? commonGameStyles.filledHeart : commonGameStyles.emptyHeart}`}
                                                >
                          ❤️
                        </span>
                                            ))}
                                        </div>
                                    </>
                                )}

                                <div className={`${styles.cardCornerIcon} ${styles["top-left"]}`} style={{ color: currentCardIcon.color }}>
                                    {currentCardIcon.symbol}
                                </div>
                                <div className={`${styles.cardCornerIcon} ${styles["bottom-right"]}`} style={{ color: currentCardIcon.color }}>
                                    {currentCardIcon.symbol}
                                </div>

                                <p className={styles.progressText}>Question {current + 1} of {questions.length}</p>
                                <div className={styles.progressWrapper}>
                                    <div className={styles.progressBar} style={{ width: `${((current + 1) / questions.length) * 100}%` }} />
                                </div>

                                <div className={styles.transliterationQuestion}>
                                    <p className={styles.transliterationText}>{currentQ?.transliteration}</p>
                                    <p className={styles.arabicWordInQuestion}>{currentQ?.correctArabicWord}</p>
                                </div>

                                <div className={styles.options}>
                                    {currentQ?.options?.map((option, index) => (
                                        <button
                                            key={index}
                                            onClick={() => handleOptionClick(option)}
                                            className={`${styles.optionButton} ${
                                                selectedOption !== null
                                                    ? option === currentQ.correctAnswerText
                                                        ? styles.correct
                                                        : option === selectedOption
                                                            ? styles.incorrect
                                                            : ""
                                                    : ""
                                            }`}
                                            disabled={selectedOption !== null || showGameOverPopup}
                                        >
                                            {option}
                                        </button>
                                    ))}
                                </div>

                                {/* Feedback overlay (appears after answer) */}
                                <div
                                    style={{
                                        position: "absolute",
                                        top: "50%",
                                        left: "50%",
                                        transform: "translate(-50%, -50%)",
                                        backgroundColor: "rgba(0, 0, 0, 0.75)",
                                        padding: "2rem",
                                        borderRadius: "1rem",
                                        boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                                        width: "90%",
                                        maxWidth: "600px",
                                        zIndex: 100,
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        textAlign: "center",
                                        gap: "1rem",
                                        color: "#eee",
                                        opacity: showFeedback ? 1 : 0,
                                        visibility: showFeedback ? "visible" : "hidden",
                                        transition: "opacity 0.5s ease-in-out, visibility 0.5s",
                                    }}
                                >
                                    <p style={{ fontSize: "1.2rem", fontWeight: "bold", color: isCorrect ? "green" : "red", margin: 0 }}>
                                        {isCorrect ? "✅ Correct, MashaAllah!" : "❌ Incorrect."}
                                    </p>
                                    <p style={{ margin: 0 }}>
                                        Correct Meaning: <strong>{currentQ?.correctAnswerText}</strong>
                                    </p>
                                    <p style={{ margin: 0, fontSize: "0.9rem", color: "#ccc" }}>
                                        Arabic Word: {currentQ?.correctArabicWord}
                                    </p>
                                    <button onClick={handleNextQuestion} className={styles.nextButton} disabled={showGameOverPopup}>
                                        {current + 1 < questions.length ? "Next Question" : "View Results"}
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <p className={styles.message}>No flashcards available after selection. Please try again.</p>
                        )
                    )}
                </div>
            </div>

            {/* Popups and feedback */}
            <GamePopups
                showStreakPopup={showStreakPopup}
                streakMessage={streakMessage}
                showEncouragementPopup={showEncouragementPopup}
                encouragementMessage={encouragementMessage}
            />

            {/* Feedback modal: only show in standalone mode */}
            {!inJourney && (
                <FeedbackModal
                    isOpen={showFeedbackModal}
                    onClose={handleCloseFeedbackModal}
                    onDismissPermanently={handleDismissFeedbackPermanently}
                    formUrl={FEEDBACK_FORM_URL}
                    gameName="Flashcards"
                />
            )}
        </div>
    );
}

export default Flashcards;
